import { digitsOnly, inputDigitsOnly, money, number } from "./numbers";
import './string';
import lodashGet from "lodash/get.js";
import * as url from "./url";
import FormDataWrapper from "./form-data-wrapper";

/**
 * To use it, you need some moves:
 * 1- First   : you should have global variable:`let my_debounce;`
 * 2- Next    : make your own function: `function my_debounce_fn(callback,...data){...}`
 * 3- Next    : in your method, check if it's not existed yet, use this helper method to create it: `if(!my_debounce){my_debounce=Salla.helpers.debounce((fn, ...inner_data) => fn(...inner_data), 500)}`
 * 4- Next    : then delay the passed callback: `my_debounce(callback, ...data);`
 * 5- Finally : use it: `my_debounce(() =>{...yourRepeatableLogic})`
 * will be something like:
 *
 * ```
 * let my_debounce;
 * function my_debounce_fn(callback,...data){
 *     if(!my_debounce){
 *          //inline method
 *          my_debounce=Salla.helpers.debounce((fn, ...inner_data) => fn(...inner_data), 500);
 *     }
 *     return my_debounce(callback, ...data);
 * }
 * // use it like:
 * my_debounce_fn((my_data)=>Salla.log(my_data), 'hello there - 1');
 * my_debounce_fn((my_data)=>Salla.log(my_data), 'hello there - 2');
 * my_debounce_fn((my_data)=>Salla.log(my_data), 'hello there - 3');
 * my_debounce_fn((my_data)=>Salla.log(my_data), 'hello there - 4');
 * //output=> `hello there - 4`
 * ```
 * 😎Or just use Salla appHelpers:`app.debounce((my_data)=>Salla.log(my_data), 'hello there - *')`
 *
 * @param fn
 * @param delay
 * @return {function(...[*]): Promise<unknown>}
 */
function debounce(fn, delay) {
  delay = delay || 100;
  let resolves = [];
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      let result = fn(...args);
      resolves.forEach((r) => r(result));
      resolves = [];
    }, delay);
    //@ts-ignore
    return new Promise((r) => resolves.push(r));
  };
}

/**
 * Use It When You want to grab data from input names with brackets Ex.
 * <input name="products[0][id]" value...>
 * <input name="products[0][name]" value...>
 * <input name="products[1][id]" value...>
 * <input name="products[1][name]" value...>
 * will be like: {products:[{id:.., name:..},{id:.., name:..}]}
 * @param name
 * @param value
 * @param dataset
 * @return {{name, value}|{name, value: (*|[*, *]|[string, *]|{}|[*])}}
 */
function getInputDataWithName(name, value, dataset = {}) {
  if (name.includes("[")) {
    let nameLevels = name.split("]").join("").split("[");
    const parentKey = nameLevels[0];
    // Check if dataset is an object before accessing its properties
    const parentData = dataset && typeof dataset === 'object' ? dataset[parentKey] : undefined;
    return { name: parentKey, value: getLeveledData(nameLevels.slice(1), value, parentData) };
  }
  return { name: name, value: value };
}

/**
 * in case input name is array for example "jamal[4655]" let's fix it
 * This Helper method to get suitable array data, Ex.
 * jamal[]=1
 * jamal[]=2
 * final result will be: {jamal:[1,2]}
 *
 * @param {String[]} nameLevels
 * @param {string|mixed} value
 * @param {{}|undefined} existedData
 * @return {*[]|*|(string|*)[]|{}}
 */
function getLeveledData(nameLevels, value, existedData) {
  // TODO: typo, rename it to `getLabeledData`
  let currentName = nameLevels[0];

  //it's an array, so add value ass array
  if (existedData && nameLevels.length == 0) {
    if (Array.isArray(existedData)) {
      existedData.push(value);
      return existedData;
    }

    //todo::handle in case it's object
    return [existedData, value];
  }

  if (Array.isArray(existedData)) {
    existedData.push(value);
    return existedData;
  }

  if (typeof existedData === "string") {
    return [existedData, value];
  }

  //it's nested data
  if (existedData) {
    existedData[currentName] = getLeveledData(nameLevels.slice(1), value, existedData[currentName]);
    return existedData;
  }

  if (currentName) {
    //@ts-ignore
    return { [currentName]: getLeveledData(nameLevels.slice(1), value) };
  }

  //handling "[]" without name
  return currentName === "" ? [value] : value;
}

/*
window.onbeforeunload = function (event) {
  Salla.document.event.leaving(event);
  if (!Salla.config.get("canLeave")) {
    event.preventDefault();
    return false;
  }
};
*/
export function setNested(object, key, value) {
  let schema = object;
  let keys = key.split(".");
  let len = keys.length;
  for (let i = 0; i < len - 1; i++) {
    let elem = keys[i];
    if (!schema[elem]) schema[elem] = {};
    schema = schema[elem];
  }

  schema[keys[len - 1]] = value;
  return object;
}

export function getNested(object, key, default_) {
    //we have issue on the import because on of the new packages we upgraded pnpm, this is last time it was working without it: https://github.com/SallaApp/twilight/blob/0215beb4a1f89fb233df9b173134d7571882f5fe/pnpm-lock.yaml
    let data = lodashGet.default
    ? lodashGet.default(object, key)
    : lodashGet(object, key);
    return data !== undefined ? data : default_;
}

export function hasApplePay(): boolean {
  try {
    return !!window.ApplePaySession?.canMakePayments();// && !/snapchat/i.test(navigator.userAgent);
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function isAppleDevice(): boolean {
  try {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    // Detect iOS devices
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    // Detect macOS
    const isMac = /Mac/i.test(platform);
    return isIOS || isMac;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function isIOSDevice(): boolean {
  try {
    const ua = navigator.userAgent || '';
    return /iPhone|iPad|iPod/i.test(ua);
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function isIframe() {
  return window.self !== window.top
}

/**
 * Detects whether the current browser is a WebView (embedded browser inside a native app).
 *
 * iOS detection: WebKit is present but the "Safari" token is absent in the UA string,
 * which is the standard indicator of an iOS WKWebView.
 *
 * Android detection: checks for the official "wv" token added in Android 5.0+.
 * Falls back to matching Android + AppleWebKit without Chrome for older WebViews.
 */
export function isWebView(): boolean {
  try {
    if (typeof window.navigator === 'undefined') return false;

    const ua = window.navigator.userAgent;

    // iOS: WebKit present but Safari token absent
    const isIOSWebView = /iP(hone|od|ad)/.test(ua)
      && /AppleWebKit/.test(ua)
      && !/Safari/.test(ua);

    // Android: official 'wv' token (5.0+),
    // fallback for older: has Android + AppleWebKit but no Chrome
    const isAndroidWebView =
      /\bwv\b/.test(ua)
      || (/Android/.test(ua) && /AppleWebKit/.test(ua) && !/Chrome/.test(ua));

    return isIOSWebView || isAndroidWebView;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function isPreview() {
  return isIframe()
}

/**
 * Factory function to create a wrapped object with proxy support
 */
export function createFormDataWrapper<T extends Record<string, any> | FormData>(
  data: T
): any {
  const wrapper = new FormDataWrapper(data);
  return wrapper.getProxy();
}

/**
 * @type {{debounce: (function(*, *=): function(...[*]): Promise<unknown>), inputData: ((function(*=, *=, *): ({name, value}|{name, value: (*|*[]|(string|*)[]|{})}))|*), digitsOnly: ((function(*=, *=): (undefined|*))|*)}}
 */
export default {
  // Numbers helpers
  digitsOnly: digitsOnly,
  inputDigitsOnly: inputDigitsOnly,
  number: number,
  money: money,

  //environment and context helpers
  isIframe,
  isPreview,

  // Nested objects helpers
  setNested: setNested,
  getNested: getNested,
  inputData: getInputDataWithName,

  // Url helpers
  url: url,
  addParamToUrl: url.addParamToUrl,

  // other
  debounce: debounce,
  hasApplePay: hasApplePay,
  createFormDataWrapper: createFormDataWrapper,
  
  isAppleDevice: isAppleDevice,
  isIOSDevice: isIOSDevice,
  isWebView: isWebView,
};
